import type { View } from "@pimo/pimo-app-builder";
import type { DragAndDropLayout } from "@pimo/pimo-components";
import {
  ChipCell,
  DateCell,
  DerivePMTableProps,
  PMSmallNumberCardEditSchema,
  PMTableEventNames,
  PMTableEventPayload,
  ProgressBarCell,
  StatusIndicatorCell,
  TableUpdateStatusCell,
  TextCardCell,
} from "@pimo/pimo-components";
import PMTable from "@pimo/pimo-components/src/lib/pm-components/pm-table/pm-table";
import type { Configuration } from "product-test-types";

import type { TestAppState } from "../../app";
import { getLocalizedCardCopy } from "../../utils/get-localized-card-copy";
import { getPluralOfProjectName } from "../../utils/get-plural-of-project-name";

type ProjectsTableCellComponent =
  | typeof TextCardCell
  | typeof ChipCell
  | typeof StatusIndicatorCell
  | typeof ProgressBarCell
  | typeof DateCell
  | typeof TableUpdateStatusCell;

type ProjectsTableDefinition = Array<{
  component: ProjectsTableCellComponent;
}>;

export type ProjectsTableColumnProps = DerivePMTableProps<
  PMTable<ProjectsTableDefinition>
>["data"][number]["columnProps"];

type AddProjectsTableComponentArgs = {
  view: View<TestAppState, DragAndDropLayout>;
  configuration?: Configuration;
};

export function addProjectsTableComponent({
  view,
  configuration,
}: AddProjectsTableComponentArgs) {
  const enabledProjectCustomFieldsForTable =
    configuration?.customFields?.filter(
      (field) =>
        field?.enabled &&
        field?.flag !== "delete" &&
        (field?.purpose === "projectBasic" ||
          field?.purpose === "projectReporting")
    ) ?? [];

  const baseProjectsTableDefinition = [
    { component: TextCardCell },
    { component: TextCardCell },
    { component: TextCardCell },
    { component: ChipCell },
    { component: StatusIndicatorCell },
    { component: StatusIndicatorCell },
    { component: ProgressBarCell },
    { component: DateCell },
    { component: DateCell },
    { component: TableUpdateStatusCell },
  ];

  const projectsTableDefinition: ProjectsTableDefinition = [
    ...baseProjectsTableDefinition,
    ...enabledProjectCustomFieldsForTable.map((field) => ({
      component:
        field.type === "Date"
          ? DateCell
          : field.type === "Select"
            ? ChipCell
            : TextCardCell,
    })),
  ];

  const projectsTable = new PMTable<typeof projectsTableDefinition>(
    projectsTableDefinition
  );

  const projectsTableComponent = view.addComponent<
    DerivePMTableProps<typeof projectsTable>,
    PMTableEventNames,
    PMTableEventPayload
  >({
    component: projectsTable,
    layoutProps: {
      componentId: "projectsTableComponent",
      labels: ["#ofProjects"],
      description: "",
      title: "",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  projectsTableComponent.mapLayoutProps(({ language, settings }) => {
    const projectNamePlural = getPluralOfProjectName(
      settings?.configuration?.projectName,
      language
    );

    return {
      componentId: "projectsTableComponent",
      labels: ["#ofProjects"],
      description: getLocalizedCardCopy(language, "overviewProjectsTable", {
        projectNamePlural,
      }).description,
      title: getLocalizedCardCopy(language, "overviewProjectsTable", {
        projectNamePlural,
      }).title,
      editSchema: PMSmallNumberCardEditSchema,
    };
  });

  return {
    projectsTable,
    projectsTableComponent,
    enabledProjectCustomFieldsForTable,
  };
}
